'use client'

import {
  Headphones,
  Loader2,
  Maximize2,
  MessageCircle,
  Mic,
  MicOff,
  Minimize2,
  Paperclip,
  RefreshCw,
  Send,
  User,
  WifiOff,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import ChatMarkdown from './ChatMarkdown'
import { useMergeRefs } from 'use-callback-ref'
import type { ChatCoreRenderProps } from './ChatCore'
import { TypingIndicator } from './LoadingSpinner'
import { SourcesDisplay } from './SourcesDisplay'
import { MessageAttachments } from './MessageAttachments'
import FlowStatusIndicator from './FlowStatusIndicator'
import FormNode from './FormNode'
import NextImage from 'next/image'

const CHAT_OPEN_STORAGE_KEY = 'primal_chat_widget_is_open'

interface FloatingChatLayoutProps {
  renderProps: ChatCoreRenderProps
  isStaffLoggedIn?: boolean
  shouldOpenChat?: boolean // Flag to trigger auto-open chat
  onChatOpened?: () => void // Callback when chat is opened
}

export function FloatingChatLayout({
  renderProps,
  isStaffLoggedIn = false,
  shouldOpenChat = false,
  onChatOpened,
}: FloatingChatLayoutProps) {
  const {
    messages,
    inputText,
    isLoading,
    sessionId,
    uploadedImage,
    integrationStatus,
    isHandedOver,
    agentName,
    showHandoverButton,
    hasNewMessage,
    wsConnectionIssue,
    wsConnectionMessage,
    wsReconnecting,
    retryWebSocket,
    isFlowActive,
    flowSession,
    isProcessingFlow,
    flowError,
    hasDefaultFlow,
    defaultBot,
    isRecording,
    isProcessingVoice,
    voiceError,
    isVoiceSupported,
    isDragActive,
    getRootProps,
    getInputProps,
    setInputText,
    sendMessage,
    clearUploadedImage,
    requestHandover,
    toggleRecording,
    clearVoiceError,
    resetFlow,
    clearFlowError,
    messagesContainerRef,
    inputRef,
    t,
    executeFlow,
  } = renderProps

  // Always start with false to ensure consistent SSR/hydration
  const [isOpen, setIsOpen] = useState(false)
  // Track whether the chat was opened by auto-open (entry node) vs user action.
  // Auto-open should not persist to localStorage across page loads.
  const autoOpenedRef = useRef(false)

  // Sync with localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CHAT_OPEN_STORAGE_KEY)
      if (saved === 'true') {
        setIsOpen(true)
      }
    } catch (error) {
      console.error('Failed to read chat open state:', error)
    }
  }, [])
  const [isMaximized, setIsMaximized] = useState(false)

  const hasActiveBot = defaultBot?.status === 1
  const hasActiveFlow = isFlowActive || hasDefaultFlow === true
  const shouldHideGuestChat = !hasActiveBot && !hasActiveFlow

  const effectiveIsOpen = (shouldOpenChat && !isStaffLoggedIn) || isOpen
  const effectiveIsMaximized = effectiveIsOpen && isMaximized

  const hasAssistantNewMessage = useMemo(() => {
    if (effectiveIsOpen || messages.length === 0) {
      return false
    }
    const lastMessage = messages[messages.length - 1]
    return lastMessage.role === 'assistant'
  }, [effectiveIsOpen, messages])

  // Use separate refs - don't merge drag and drop with messages container
  const messagesRef = messagesContainerRef

  // Also scroll to bottom when user sends a message (on form submit)
  const scrollToBottom = useCallback(() => {
    console.log('scrolltobottom', { messages })
    if (messages.length > 0) {
      setTimeout(() => {
        if (!messagesContainerRef.current) return

        const container = messagesContainerRef.current
        // Try to find the last message element by data attribute
        const messageElements = container.querySelectorAll('[data-message-id]')
        if (messageElements.length > 0) {
          const lastMessageElement = messageElements[messageElements.length - 1]
          lastMessageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          })
        } else {
          // Fallback to scroll to bottom
          container.scrollTop = container.scrollHeight
        }
      }, 100) // Small delay to ensure DOM is updated
    }
  }, [messages, messagesContainerRef])

  // Auto-scroll to bottom when any new message arrives or user sends message
  useEffect(() => {
    if (messagesContainerRef.current) {
      scrollToBottom()
    } else {
      // If ref is not ready, try again after a short delay
      const timer = setTimeout(() => {
        if (messagesContainerRef.current) {
          scrollToBottom()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [messages, isOpen, isStaffLoggedIn, scrollToBottom, messagesContainerRef])

  // DISABLED: Auto-open chat when a session is created and there are messages
  // This existing behavior has been disabled to give precise control over when chat opens.
  // Now only entry nodes with openChat=true will auto-open the chat.
  // useEffect(() => {
  //   if (sessionId && messages.length > 0 && !isOpen) {
  //     console.log('💬 [FLOATING CHAT] Auto-opening chat due to new messages (existing behavior)')
  //     setIsOpen(true)
  //   }
  // }, [sessionId, messages.length, isOpen])

  useEffect(() => {
    if (shouldOpenChat) {
      if (!isStaffLoggedIn) {
        autoOpenedRef.current = true
        setIsOpen(true)
        onChatOpened?.()
      }
    }
  }, [isStaffLoggedIn, onChatOpened, shouldOpenChat])

  useEffect(() => {
    if (effectiveIsOpen) {
      inputRef.current?.focus()
    }
  }, [effectiveIsOpen, inputRef])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    // Don't persist auto-opened state; only persist explicit user actions
    if (autoOpenedRef.current) {
      autoOpenedRef.current = false
      return
    }
    try {
      localStorage.setItem(CHAT_OPEN_STORAGE_KEY, isOpen ? 'true' : 'false')
    } catch (error) {
      console.error('Failed to persist chat open state:', error)
    }
  }, [isOpen])

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    console.log('🎯 [FLOATING CHAT] handleSubmit called with:', {
      inputText,
      inputTextLength: inputText.length,
      hasUploadedImage: !!uploadedImage,
      uploadedImageUrl: uploadedImage?.url,
    })
    e.preventDefault()
    if (inputText.length > 0 || uploadedImage) {
      console.log(
        '🎯 [FLOATING CHAT] About to call sendMessage with:',
        inputText,
        uploadedImage?.url
      )
      sendMessage(inputText, uploadedImage?.url)
      // Scroll to bottom after sending message
      scrollToBottom()
    } else {
      console.log('🎯 [FLOATING CHAT] Not sending message - no text or image')
    }
  }

  if (shouldHideGuestChat) {
    return null
  }

  const handleOpenChat = () => {
    if (isStaffLoggedIn) return
    setIsOpen(true)
    onChatOpened?.()
  }

  const handleCloseChat = () => {
    setIsMaximized(false)
    setIsOpen(false)
  }

  if (!effectiveIsOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50 text-gray-600">
        <button
          type="button"
          onClick={isStaffLoggedIn ? undefined : handleOpenChat}
          className={`relative p-4 rounded-full shadow-lg transition-all duration-200 ${
            isStaffLoggedIn
              ? 'bg-gray-500 text-white cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105'
          }`}
          aria-label={isStaffLoggedIn ? 'Chat disabled for staff' : 'Open chat'}
          data-testid="chat-open-button"
          disabled={isStaffLoggedIn}
          title={
            isStaffLoggedIn ? 'Staff members cannot use the chat' : 'Open chat'
          }
        >
          <MessageCircle className="w-6 h-6" />
          {!isStaffLoggedIn && (hasAssistantNewMessage || hasNewMessage) && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          )}
        </button>
      </div>
    )
  }

  return (
    <div
      className={`fixed z-50 transition-all duration-200 ${
        effectiveIsMaximized ? 'inset-4' : 'bottom-4 right-4'
      }`}
      data-maximized={effectiveIsMaximized ? 'true' : 'false'}
    >
      <div
        className={`bg-white rounded-lg shadow-2xl border transition-all duration-200 flex flex-col ${
          effectiveIsMaximized
            ? 'w-full h-full'
            : 'max-w-[calc(100vw-4rem)] w-140 h-[32rem]'
        }`}
      >
        {/* Header */}
        <div className="bg-blue-600 text-white p-3 rounded-t-lg flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium text-sm">Assistant</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIsMaximized(prev => !prev)}
                className="p-1 hover:bg-blue-700 rounded"
                title={effectiveIsMaximized ? 'Minimize' : 'Maximize'}
              >
                {effectiveIsMaximized ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </button>
              <button
                type="button"
                onClick={handleCloseChat}
                className="p-1 hover:bg-blue-700 rounded"
                title="Close chat"
                data-testid="close-chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Agent Status & Handover Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Removed "Connected to agent" indicator - agent join message shown in chat */}
            </div>
          </div>
        </div>

        {wsConnectionIssue && (
          <div className="flex items-center justify-between gap-2 bg-amber-100 text-amber-800 border-b border-amber-200 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              <WifiOff className="w-3.5 h-3.5" />
              <span>
                {wsConnectionMessage ??
                  'Real-time updates are temporarily unavailable. Retrying connection…'}
              </span>
            </div>
            <button
              type="button"
              onClick={retryWebSocket}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-amber-800 hover:text-amber-900 hover:bg-amber-200 transition-colors"
            >
              {wsReconnecting ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              <span className="font-medium">Retry</span>
            </button>
          </div>
        )}

        {/* Flow Status Indicator - Hide when user is handed over to agent */}
        {/* Show indicator when: flow is active, there's an error, or a default flow exists */}
        {(isFlowActive || flowError || hasDefaultFlow !== null) &&
          !isHandedOver && (
            <div className="px-2 py-2 border-b">
              <FlowStatusIndicator
                isFlowActive={isFlowActive}
                flowSession={flowSession}
                isProcessingFlow={isProcessingFlow}
                flowError={flowError}
                hasDefaultFlow={hasDefaultFlow}
                defaultBot={defaultBot}
              />
            </div>
          )}

        {/* Messages */}
        <div className="relative flex-1 min-h-0">
          {/* Global drag detection area - invisible but detects drags */}
          <div
            {...getRootProps()}
            className="absolute inset-0 z-0"
            style={{ pointerEvents: isDragActive ? 'auto' : 'none' }}
          >
            <input {...getInputProps()} />
          </div>

          {/* Drag and drop overlay - only shows when dragging */}
          {isDragActive && (
            <div className="absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center z-10 pointer-events-none">
              <p className="text-blue-600 font-medium">Drop files here...</p>
            </div>
          )}

          <div
            ref={messagesRef}
            className="chat-body h-full overflow-y-auto p-4 space-y-4 relative z-5"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                data-message-id={message.id}
                data-testid="chat-message"
                data-message-role={message.role}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                } ${message.role === 'user' ? 'sent-message' : 'received-message'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : message.role === 'agent'
                        ? 'bg-green-100 text-green-800 border border-green-200 agent-message'
                        : 'bg-gray-100 text-gray-800 bot-message'
                  } flex flex-col gap-2`}
                >
                  {message.role === 'agent' && message.agentName && (
                    <div className="flex items-center mb-1">
                      <User className="w-3 h-3 mr-1 text-green-600" />
                      <span className="text-xs font-medium text-green-600">
                        {message.agentName}
                      </span>
                    </div>
                  )}
                  <MessageAttachments
                    attachments={message.attachments}
                    legacyImageUrl={message.imageUrl}
                    align="none"
                  />

                  {/* Audio player for audio messages */}
                  {message.audioUrl && (
                    <audio
                      controls
                      className="w-full max-w-sm"
                      preload="metadata"
                    >
                      <source src={message.audioUrl} type="audio/webm" />
                      <source src={message.audioUrl} type="audio/mpeg" />
                      <source src={message.audioUrl} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                  )}

                  {/* File download for document messages */}
                  {message.fileUrl && (
                    <a
                      href={message.fileUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors file-link"
                      data-testid="file-download"
                    >
                      <Paperclip className="w-4 h-4" />
                      <span>{message.fileName || 'Download file'}</span>
                    </a>
                  )}

                  {message.content &&
                    message.content.trim() !== '' &&
                    !message.audioUrl && (
                      <ChatMarkdown enableYouTubeEmbed>
                        {message.content}
                      </ChatMarkdown>
                    )}

                  {/* Display sources if available */}
                  {message.sources && message.sources.length > 0 && (
                    <SourcesDisplay sources={message.sources} />
                  )}

                  {/* Menu Options as Buttons */}
                  {message.menuOptions && message.menuOptions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {message.menuOptions.map(option => {
                        const optionDisabled = !!message.menuOptionsDisabled
                        const optionClasses = optionDisabled
                          ? 'w-full text-left px-3 py-2 bg-blue-600 border border-blue-700 rounded-lg text-sm text-white cursor-not-allowed'
                          : 'w-full text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-800 transition-colors'

                        return (
                          <button
                            key={option.id}
                            type="button"
                            disabled={optionDisabled}
                            aria-disabled={optionDisabled}
                            data-option={(option.value ?? option.label)
                              .toLowerCase()
                              .replace(/\s+/g, '-')}
                            onClick={() => {
                              if (optionDisabled) {
                                return
                              }
                              const flowValue =
                                option.value ?? option.url ?? option.label
                              if (option.url) {
                                try {
                                  window.open(
                                    option.url,
                                    '_blank',
                                    'noopener,noreferrer'
                                  )
                                } catch (error) {
                                  console.error(
                                    'Failed to open menu option URL',
                                    error
                                  )
                                }
                              }
                              // Show label to user but send value to flow
                              sendMessage(option.label, undefined, {
                                flowInput: flowValue,
                              })
                            }}
                            className={`menu-option ${optionClasses}`}
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {message.paymentRequest?.paymentUrl && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            window.open(
                              message.paymentRequest?.paymentUrl,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          } catch (error) {
                            console.error('Failed to open payment link', error)
                          }
                        }}
                        className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                      >
                        {message.paymentRequest.buttonText || 'Pay Now'}
                      </button>
                    </div>
                  )}

                  {/* Form Node */}
                  {message.formNode && (
                    <div className="mt-3">
                      <FormNode
                        nodeId={message.formNode.nodeId}
                        config={message.formNode.config}
                        sessionId={sessionId || ''}
                        onSubmit={formData => {
                          console.log('Form submitted:', formData)
                        }}
                        onCancelAction={actionValue => {
                          void executeFlow(
                            actionValue,
                            message.formNode!.nodeId
                          )
                        }}
                        onSkipAction={actionValue => {
                          void executeFlow(
                            actionValue,
                            message.formNode!.nodeId
                          )
                        }}
                        onNext={(nextMsg?: string) => {
                          // Advance the flow after successful form submission
                          void executeFlow('', message.formNode!.nodeId)
                        }}
                      />
                    </div>
                  )}

                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp
                      ? new Date(message.timestamp).toLocaleTimeString()
                      : ''}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div data-message-id="loading" className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg">
                  <TypingIndicator />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* File Preview */}
        {uploadedImage && (
          <div className="px-4 py-3 bg-gray-50 border-t flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="relative">
                {uploadedImage.file?.type?.startsWith('audio/') ||
                uploadedImage.file?.name?.match(
                  /\.(mp3|wav|webm|ogg|m4a)$/i
                ) ? (
                  <div className="w-10 h-10 bg-blue-100 flex items-center justify-center rounded">
                    <Headphones className="w-6 h-6 text-blue-600" />
                  </div>
                ) : uploadedImage.file?.type?.startsWith('image/') ? (
                  <NextImage
                    src={uploadedImage.url}
                    alt="Preview"
                    width={40}
                    height={40}
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 flex items-center justify-center rounded">
                    <Paperclip className="w-6 h-6 text-gray-600" />
                  </div>
                )}
                {uploadedImage.isUploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                )}
              </div>
              <span className="text-sm text-gray-600">
                {uploadedImage.uploadError
                  ? uploadedImage.uploadError
                  : uploadedImage.isUploading
                    ? `Uploading ${uploadedImage.file?.type?.startsWith('audio/') ? 'audio' : uploadedImage.file?.type?.startsWith('image/') ? 'image' : 'file'}...`
                    : `${uploadedImage.file?.name || (uploadedImage.file?.type?.startsWith('audio/') ? 'Audio' : uploadedImage.file?.type?.startsWith('image/') ? 'Image' : 'File')} ready`}
              </span>
              <button
                type="button"
                onClick={clearUploadedImage}
                className="text-red-500 hover:text-red-700 disabled:text-gray-400"
                disabled={uploadedImage.isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t flex-shrink-0 space-y-3">
          <form onSubmit={handleSubmit} className="flex items-center space-x-2">
            <input
              ref={inputRef}
              type="text"
              data-testid="chat-message-input"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e as any)
                }
              }}
              placeholder={t('placeholder') || 'Type your message...'}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800"
              disabled={isLoading}
            />

            <button
              type="button"
              onClick={() =>
                document.getElementById('floating-file-input')?.click()
              }
              className="bg-gray-200 hover:bg-gray-300 disabled:text-gray-500 text-gray-700 p-2 rounded-lg transition-colors"
              disabled={isLoading}
              title="Attach file"
              data-testid="upload-button"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            <input
              id="floating-file-input"
              type="file"
              accept="image/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
              onChange={async e => {
                const file = e.target.files?.[0]
                if (file) {
                  await renderProps.onDrop([file])
                }
              }}
              className="hidden"
            />

            <button
              type="button"
              onClick={toggleRecording}
              className={`p-2 rounded-lg transition-colors ${
                !isVoiceSupported
                  ? 'bg-gray-200 text-gray-700 cursor-not-allowed'
                  : isRecording
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
              disabled={isLoading || isProcessingVoice || !isVoiceSupported}
              title={
                !isVoiceSupported
                  ? 'Voice recording not supported'
                  : isRecording
                    ? 'Stop recording'
                    : 'Start recording'
              }
              data-testid="voice-button"
            >
              {isRecording ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>

            <button
              type="submit"
              data-testid="chat-send-button"
              disabled={
                isLoading ||
                uploadedImage?.isUploading === true ||
                (!inputText.trim() && !uploadedImage) ||
                (!inputText.trim() && !!uploadedImage?.uploadError)
              }
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:text-gray-900 text-white p-2 rounded-lg transition-colors"
              title="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          {/* Voice error display */}
          {voiceError && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
              <div className="flex items-start justify-between">
                <span className="flex-1">{voiceError}</span>
                <button
                  type="button"
                  onClick={clearVoiceError}
                  className="ml-2 text-red-500 hover:text-red-700"
                  title="Dismiss error"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Voice processing indicator */}
          {isProcessingVoice && (
            <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2" />
                Processing voice recording...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
